import { useState, useCallback } from "react";
import { BlogEditor } from "./BlogEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveBlogExcerpt } from "@/lib/blog-excerpt";
import { excerptForEdit } from "./edit-excerpt";
import {
  CircleNotch,
  FloppyDisk,
  PaperPlaneTilt,
  FileText,
  TextT,
  Tag,
  UserCircle,
} from "@phosphor-icons/react";
import { MediaPicker } from "@/components/ui/media-picker";
import { BlogCategorySelect } from "@/components/blog/BlogCategorySelect";
import { TherapistCombobox } from "@/components/ui/therapist-combobox";

interface BlogPostFormProps {
  initialData?: {
    id?: string;
    title?: string;
    excerpt?: string;
    content?: string;
    category?: string;
    coverImageUrl?: string | null;
    status?: string;
    therapistId?: string | null;
    /** Ime povezanog terapeuta bez obzira na status (samo admin odgovor). */
    linkedTherapistName?: string | null;
  };
  userRole: "admin" | "therapist";
  onSubmit: (data: {
    title: string;
    content: string;
    /** Omitted on edit when the stored excerpt stays (e.g. hand-written WP excerpt). */
    excerpt?: string;
    category: string;
    /** null on edit means "cover removed"; undefined on create means "no cover". */
    coverImageUrl?: string | null;
    status: string;
    therapistId?: string | null;
  }) => Promise<void>;
  isSubmitting?: boolean;
}


export function BlogPostForm({
  initialData,
  userRole,
  onSubmit,
  isSubmitting,
}: BlogPostFormProps) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [content, setContent] = useState<object | null>(() => {
    if (initialData?.content) {
      try {
        return typeof initialData.content === "string"
          ? JSON.parse(initialData.content)
          : initialData.content;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [coverImageUrl, setCoverImageUrl] = useState(
    initialData?.coverImageUrl || ""
  );
  const [category, setCategory] = useState(initialData?.category || "");
  const [therapistId, setTherapistId] = useState<string | null>(
    initialData?.therapistId ?? null
  );
  const handleContentChange = useCallback((newContent: object) => {
    setContent(newContent);
  }, []);

  const handleSubmit = async (status: string) => {
    if (!title.trim() || !content || !category) return;

    const excerpt = initialData?.id
      ? excerptForEdit({
          previousTitle: initialData.title,
          previousExcerpt: initialData.excerpt,
          previousContent: initialData.content,
          title: title.trim(),
          content,
        })
      : resolveBlogExcerpt({ content, title: title.trim() });

    await onSubmit({
      title: title.trim(),
      content: JSON.stringify(content),
      ...(excerpt !== undefined ? { excerpt } : {}),
      category,
      // On edit an emptied picker must reach the server as null, otherwise the
      // old cover silently stays.
      coverImageUrl: coverImageUrl || (initialData?.id ? null : undefined),
      status,
      ...(userRole === "admin" ? { therapistId } : {}),
    });
  };

  const canSubmit = title.trim().length > 0 && content !== null && category !== "";

  return (
    <div className="space-y-5">
      {/* Cover image */}
      <MediaPicker
        value={coverImageUrl}
        onChange={setCoverImageUrl}
        folder="blog"
        label="Naslovna slika"
        hint="JPG, PNG ili WebP · maks. 5 MB"
        previewHeight="h-52 sm:h-64"
      />

      {/* Category */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/8">
            <Tag className="size-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <Label
              className="text-sm font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Tema
            </Label>
            <BlogCategorySelect value={category} onChange={setCategory} />
          </div>
        </div>
      </div>

      {/* Linked therapist (admin only) */}
      {userRole === "admin" && (
        <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/8">
              <UserCircle className="size-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <Label
                className="text-sm font-semibold text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Terapeut
              </Label>
              <TherapistCombobox
                value={therapistId}
                onChange={setTherapistId}
                placeholder="Bez terapeuta"
                unlistedLabel={
                  therapistId && therapistId === initialData?.therapistId
                    ? `${initialData?.linkedTherapistName ?? "Nepoznat terapeut"} (neaktivan)`
                    : undefined
                }
              />
              <p className="text-xs text-muted-foreground">
                Povezani terapeut se prikazuje kao autor članka i članak izlazi na njegovom profilu.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/8">
            <TextT className="size-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <Label
              htmlFor="title"
              className="text-sm font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Naslov članka
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Unesite naslov članka..."
              className="text-lg"
            />
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="flex items-center gap-3 border-b px-4 py-3 sm:px-5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/8">
            <FileText className="size-4 text-primary" />
          </div>
          <div>
            <p
              className="text-sm font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Sadržaj
            </p>
          </div>
        </div>
        <BlogEditor
          initialContent={content || undefined}
          onChange={handleContentChange}
        />
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-4 flex items-center justify-between rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur-sm ring-1 ring-border/50 sm:p-5">
        <p className="hidden text-sm text-muted-foreground sm:block">
          {canSubmit
            ? "Članak je spreman za objavu."
            : "Unesite naslov i sadržaj."}
        </p>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1 sm:flex-none"
          >
            {isSubmitting ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <FloppyDisk className="size-4" />
            )}
            Nacrt
          </Button>

          {userRole === "admin" ? (
            <Button
              onClick={() => handleSubmit("published")}
              disabled={isSubmitting || !canSubmit}
              className="flex-1 sm:flex-none"
            >
              {isSubmitting ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                <PaperPlaneTilt className="size-4" />
              )}
              Objavi
            </Button>
          ) : (
            <Button
              onClick={() => handleSubmit("pending")}
              disabled={isSubmitting || !canSubmit}
              className="flex-1 sm:flex-none"
            >
              {isSubmitting ? (
                <CircleNotch className="size-4 animate-spin" />
              ) : (
                <PaperPlaneTilt className="size-4" />
              )}
              Pošalji na pregled
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
